import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useRef, useMemo, useState } from 'react';
import { RiArrowUpLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import LabelGroup from '@/components/ui/LabelGroup';
import { MODE } from '@/define/robotStatus';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const [isProcessing, setProcessing] = useState(false);
  const inputRefs = useRef({
    altitude: useRef(),
    climbSpeed: useRef(),
  }).current;
  const actionCommand = useActionCommand();

  const isShowClimbSpeed = useMemo(() => {
    return robots[0].model.maker === 'ArduPilot' && robots[0].model.vehicle === 'Copter';
  }, [robots]);

  const handleBlur = (e) => {
    const { name, value } = e.target;

    inputRefs[name].current.value = Number(value).toFixed(1);
  };

  //#region Process Functions
  const process = (robot) => {
    switch (robot.model.maker) {
      case 'ArduPilot':
        switch (robot.model.vehicle) {
          case 'Copter':
            return processArduCopter(robot);
          case 'Plane':
            return processArduPlane(robot);
          default:
            return Promise.resolve();
        }
      default:
        return Promise.resolve();
    }
  };

  const processArduCopter = async (robot) => {
    const altitude = Number(inputRefs.altitude.current.value);
    const climbSpeed = Number(inputRefs.climbSpeed.current.value);

    try {
      await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.COPTER.GUIDED.id));
      await executeMavLinkCommand(robot, actionCommand.changeSpeed(2, climbSpeed));
      await executeMavLinkCommand(robot, actionCommand.takeoff(0, 0, 0, 0, altitude));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processArduPlane = async (robot) => {
    const altitude = Number(inputRefs.altitude.current.value);

    try {
      await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.PLANE.GUIDED.id));
      await executeMavLinkCommand(robot, actionCommand.takeoff(0, 0, 0, 0, altitude));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    notifier.success(`${robot.name} has taken off successfully.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to take off ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Takes off and climbs to specified altitude</div>
      <div className={cx('form')}>
        <LabelGroup htmlFor="altitude" label="Altitude">
          <div className={cx('fieldWrapper')}>
            <FieldNumber id="altitude" ref={inputRefs.altitude} name="altitude" defaultValue={20} onBlur={handleBlur} />
            <div className={cx('unit')}>m</div>
          </div>
        </LabelGroup>
        {isShowClimbSpeed && (
          <LabelGroup htmlFor="climbSpeed" label="Climb Speed">
            <div className={cx('fieldWrapper')}>
              <FieldNumber
                id="climbSpeed"
                ref={inputRefs.climbSpeed}
                name="climbSpeed"
                defaultValue={2}
                onBlur={handleBlur}
              />
              <div className={cx('unit')}>m/s</div>
            </div>
          </LabelGroup>
        )}
      </div>
      <ConfirmSlider
        width="100%"
        height={40}
        onConfirm={doCommand}
        isProcessing={isProcessing}
        labelStyle={{ fontSize: 12 }}
      />
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Takeoff';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = false;
Command.commanderOnly = true;
Command.Icon = RiArrowUpLine;
Command.Panel = Panel;

export default Command;
